/**
 * Chats + inferences API slices.
 * Leaf module (Perf 3.1.e Step 4). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { Platform } from 'react-native';
import { fetchWithAuth, ApiError, getAuthStore, API_HOST } from './client';
import { isTransientConversationLoadError } from '../conversationLoadRecovery';

// Route navigation and Work refreshes can ask for the same detail at once.
// Share only pending requests, scoped to the current login; never cache a
// completed response here, since follow-ups need fresh messages.
const pendingConversationDetails = new Map();

const CONVERSATION_EXPORT_FORMATS = {
  markdown: 'md',
  text: 'txt',
  pdf: 'pdf',
  md: 'md',
  txt: 'txt',
};

export const chatsApi = {
  fetchStarterPrompts: async () => {
    const data = await fetchWithAuth('/json/chat/starter-prompts', { timeoutMs: 45000 });
    if (data.error) throw new ApiError(data.message || 'Failed to load starter prompts', data.status || 400, data);
    return data;
  },

  pinChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/pin`, {
      method: 'POST'
    });
    const success = !data.error;
    return success;
  },

  /**
   * Respond to an auto-categorization ("Move this chat to <collection>?")
   * suggestion. Accept performs the move server-side and returns the updated
   * collection; dismiss resolves the suggestion permanently for this chat.
   * @param {string} conversationId
   * @param {'accept'|'dismiss'} action
   * @returns {Promise<{collection?: Object}>}
   */
  respondToCollectionSuggestion: async (conversationId, action) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/collection-suggestion/respond`, {
      method: 'POST',
      body: { action },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to respond to collection suggestion', 400, data);
    }

    // Accept responses include the updated collection (same shape as
    // collectionsApi.addConversation); dismiss responses have no payload.
    return { collection: data.collection || null };
  },

  /**
   * Download a conversation export as a binary blob.
   * Goes through fetchWithAuth so timeouts, 401 refresh, and auth hydration
   * behave like every other request. The older binary helper swallowed
   * AbortError (so a slow PDF looked like a no-op) and ignored the
   * server's Content-Disposition filename (CJK titles became ".pdf").
   * @param {string} conversationId
   * @param {'md'|'pdf'|'txt'|'markdown'|'text'} format
   * @returns {Promise<{blob: Blob, contentType: string, filename: string|null}>}
   */
  downloadConversationExport: async (conversationId, format) => {
    const serverFormat = CONVERSATION_EXPORT_FORMATS[format] || format;
    return await fetchWithAuth(
      `/json/export/c/${conversationId}/${serverFormat}`,
      // PDF rendering is synchronous server-side work (markdown build +
      // pdfkit layout) and can take well over a minute for very large
      // conversations with many archived messages. Markdown/text are
      // near-instant, so only PDF needs the extended timeout.
      { method: 'GET', timeoutMs: serverFormat === 'pdf' ? 180000 : 60000, responseType: 'blob' }
    );
  },

  /**
   * Download a single message export as a binary blob.
   * @param {string} conversationId
   * @param {string} messageId
   * @param {'plain'|'markdown'|'pdf'} format
   * @returns {Promise<{blob: Blob, contentType: string, filename: string|null}>}
   */
  downloadMessageExport: async (conversationId, messageId, format) => {
    return await fetchWithAuth(
      `/json/conversations/${conversationId}/messages/${messageId}/export?format=${encodeURIComponent(format)}`,
      { method: 'GET', timeoutMs: 60000, responseType: 'blob' }
    );
  },

  getConversationById: async (conversationId) => {
    const auth = getAuthStore().getState();
    const key = JSON.stringify([auth.user?.id, auth.token, conversationId]);
    if (pendingConversationDetails.has(key)) return pendingConversationDetails.get(key);
    const startedAt = Date.now();
    const request = (async () => {
      try {
        const isRequestCurrent = () => {
          const current = getAuthStore().getState();
          return current.user?.id === auth.user?.id && !!current.token === !!auth.token;
        };
        const readDetail = () => fetchWithAuth(`/json/conversations/${conversationId}/detail`, {
          method: 'GET', isRequestCurrent,
        });
        let data;
        try {
          data = await readDetail();
        } catch (error) {
          // fetchWithAuth already retries transport failures. Retry gateway
          // failures once too, sharing the retry with all pending readers.
          // This is a read only: never replay a submitted chat or Work turn.
          if (!isTransientConversationLoadError(error) || error.status === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 350));
          if (!isRequestCurrent()) throw new ApiError('Session changed. Please retry.', 401);
          data = await readDetail();
        }

        if (data.error) {
          throw new ApiError(data.message || 'Failed to fetch conversation', 400, data);
        }

        // PLUS_WINBACK_PRESSURE §2: for churned-Plus users the backend
        // trims the conversation's `messages` array to the trailing N
        // visible messages and sends a sibling `plusMessageLock`
        // payload describing what was hidden. The detail endpoint
        // ALWAYS includes the field (as `null` when no lock applies)
        // so a re-subscribed user's next fetch can authoritatively
        // clear any stale truncation banner the store may hold from
        // a previous churn window. We stash it (and a marker that we
        // came from a detail fetch) onto the returned object so the
        // store knows this is an authoritative update.
        if (data.conversation && 'plusMessageLock' in data) {
          data.conversation.plusMessageLock = data.plusMessageLock || null;
          data.conversation._plusMessageLockAuthoritative = true;
        }

        return data.conversation;
      } catch (error) {
        console.error('Error fetching conversation:', error);
        throw error;
      }
    })();
    pendingConversationDetails.set(key, request);
    try {
      return await request;
    } finally {
      pendingConversationDetails.delete(key);
      if (__DEV__) console.debug(`[ChatDetailTiming] ${conversationId} ${Date.now() - startedAt}ms`);
    }
  },

  getConversationStatus: async (conversationId) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/status`, {
        method: 'GET',
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch conversation status', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error fetching conversation status:', error);
      throw error;
    }
  },

  getArchivedConversationMessages: async (conversationId, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.limit) params.set('limit', String(options.limit));
      if (options.beforeIndex !== undefined && options.beforeIndex !== null) {
        params.set('beforeIndex', String(options.beforeIndex));
      }

      const query = params.toString();
      const data = await fetchWithAuth(
        `/json/conversations/${conversationId}/messages/archive${query ? `?${query}` : ''}`,
        { method: 'GET' }
      );

      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch archived messages', 400, data);
      }

      return {
        messages: data.messages || [],
        pagination: data.pagination || {},
      };
    } catch (error) {
      console.error('Error fetching archived conversation messages:', error);
      throw error;
    }
  },

  createConversation: async (formattedMessage, collectionId = undefined, isTemporary = false, usePersonalization = false) => {
    const data = await fetchWithAuth('/json/conversations/new', {
      method: 'POST',
      body: {
        ...formattedMessage,
        collectionId,
        isTemporary,
        usePersonalization: isTemporary ? !!usePersonalization : false,
      },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to create conversation', 400, data);
    }

    return data.conversation;
  },

  forkConversation: async (conversationId, messageId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/fork`, {
      method: 'POST',
      body: { messageId },
    });

    if (data.error || !data.conversation) {
      throw new ApiError(data.message || 'Failed to fork conversation', 400, data);
    }

    return data.conversation;
  },

  createFirstConversation: async (formattedMessage) => {
    try {
      const response = await fetch(`${API_HOST}/json/conversations/new-first`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ // need stringify?
          ...formattedMessage,
        })
      });

      const data = await response.json();
      const error = data?.error;
      const conversation = data?.conversation;
      const tokens = data?.tokens;
      const user = data?.user;

      if (error || !conversation || !tokens || !user) {
        throw new ApiError(data.message || 'Failed to create first conversation', response.status, data);
      }

      // Store the tokens and user data in auth store since this creates a ghost account
      await getAuthStore().getState().setAuthData(tokens?.access, tokens?.expiresAt, tokens?.refresh, user);

      return {
        conversation: data.conversation,
        user: data.user,
        token: data.tokens?.access
      };
    } catch (error) {
      console.error('Error creating first conversation:', error);
      throw error;
    }
  },

  unpinChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/pin`, {
      method: 'DELETE'
    });
    const success = !data.error;
    return success;
  },

  markAsRead: async (conversationId) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/read`, {
        method: 'POST'
      });
      return !data.error;
    } catch (error) {
      console.error('Error marking chat as read:', error);
      return false;
    }
  },

  updateChat: async (conversationId, updates) => {
    const validFields = ['title', 'visibility', 'customControls'];
    const filteredUpdates = Object.keys(updates)
      .filter(key => validFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = updates[key];
        return obj;
      }, {});

    if (Object.keys(filteredUpdates).length === 0) {
      throw new Error('No valid updates provided');
    }

    const data = await fetchWithAuth(`/json/conversations/${conversationId}`, {
      method: 'PUT',
      body: filteredUpdates
    });

    if (data.error) {
      throw new ApiError(data.message, 400, data);
    }

    return data.conversation;
  },

  fetchChats: async (page = 1, options = {}) => {
    const { inCollectionId, filter, sort } = options;
    let url = `/json/conversations?page=${page}`;

    if (inCollectionId) {
      url += `&in_collection_id=${inCollectionId}`;
    }
    if (filter) {
      url += `&filter=${filter}`;
    }
    if (sort) {
      url += `&sort=${sort}`;
    }

    const data = await fetchWithAuth(url);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch conversations', 400, data);
    }

    return {
      chats: data.conversations,
      pagination: {
        current: data.pagination.current || data.pagination.page || 1,
        pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.perPage),
        total: data.pagination.total,
        perPage: data.pagination.perPage,
        hasMore: data.pagination.hasMore,
        // Include additional pagination info
        inCollectionId: data.pagination.in_collection_id,
        withoutCollections: data.pagination.without_collections === 'true',
        filter: data.pagination.filter,
        sort: data.pagination.sort
      },
      // Plus win-back payload for churned-Plus users; null/undefined for
      // never-Plus and current-Plus users. See backend/utils/plus_lock.js.
      plusLock: data.plusLock || null,
    };
  },

  archiveChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/archive`, {
      method: 'POST'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to archive conversation', 400, data);
    }

    return data;
  },

  bulkArchiveChats: async (conversationIds) => {
    const data = await fetchWithAuth('/json/conversations/bulk-archive', {
      method: 'POST',
      body: { conversationIds }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to bulk archive conversations', 400, data);
    }
    
    return {
      archivedCount: data.archivedCount,
      success: !data.error
    };
  },

  bulkUnarchiveChats: async (conversationIds) => {
    const data = await fetchWithAuth('/json/conversations/bulk-unarchive', {
      method: 'POST',
      body: { conversationIds }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to bulk unarchive conversations', 400, data);
    }
    
    return {
      unarchivedCount: data.unarchivedCount,
      success: !data.error
    };
  },

  bulkDeleteChats: async (conversationIds) => {
    const data = await fetchWithAuth('/json/conversations/bulk-delete', {
      method: 'DELETE',
      body: { conversationIds }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to bulk delete conversations', 400, data);
    }
    
    return {
      deletedCount: data.deletedCount,
      success: !data.error
    };
  },

  unarchiveChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/unarchive`, {
      method: 'POST'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to unarchive conversation', 400, data);
    }

    return data;
  },

  fetchExistingChatIds: async (conversationIds) => {
    const data = await fetchWithAuth('/json/conversations/existing-ids', {
      method: 'POST',
      body: { conversationIds },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to check conversations', 400, data);
    }

    return {
      existingIds: Array.isArray(data.existingIds) ? data.existingIds.map(String) : [],
    };
  },

  deleteChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}`, {
      method: 'DELETE'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete conversation', 400, data);
    }

    return data;
  },

  deleteTemporaryChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/delete-temporary-conversation`, {
      method: 'DELETE'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete temporary conversation', 400, data);
    }

    return data;
  },

  saveTemporaryChat: async (conversationId) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/save-temporary`, {
      method: 'POST',
    });

    if (data.error || !data.conversation) {
      throw new ApiError(data.message || 'Failed to save temporary conversation', 400, data);
    }

    return data.conversation;
  },

  setTemporaryChatPersonalization: async (conversationId, usePersonalization) => {
    const data = await fetchWithAuth(`/json/conversations/${conversationId}/temporary-personalization`, {
      method: 'POST',
      body: { usePersonalization: !!usePersonalization },
    });

    if (data.error || !data.conversation) {
      throw new ApiError(data.message || 'Failed to update temporary chat settings', 400, data);
    }

    return data.conversation;
  },

  saveMessage: async (conversationId, messageId) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${messageId}/save`, {
        method: 'POST'
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to save message', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  },

  unsaveMessage: async (conversationId, messageId) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${messageId}/save`, {
        method: 'DELETE'
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to unsave message', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error unsaving message:', error);
      throw error;
    }
  },

  fetchBookmarks: async (page = 1, limit = 20, { folderId, sort, search } = {}) => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (folderId) params.set('folderId', folderId);
      if (sort) params.set('sort', sort);
      if (search) params.set('search', search);
      const data = await fetchWithAuth(`/json/bookmarks?${params}`);
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch bookmarks', 400, data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw error;
    }
  },

  editMessage: async (conversationId, messageId, content) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${messageId}/edit`, {
        method: 'PUT',
        body: { content }
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to edit message', 400, data);
      }

      return data.message;
    } catch (error) {
      console.error('Error editing message:', error);
      throw error;
    }
  },

  deleteMessage: async (conversationId, messageId) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${messageId}/delete`, {
        method: 'DELETE'
      });

    if (data.error) {
        throw new ApiError(data.message || 'Failed to delete message', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  setMessagePreference: async (conversationId, messageId, preferred) => {
    try {
      const data = await fetchWithAuth(`/json/conversations/${conversationId}/messages/${messageId}/prefer`, {
        method: 'POST',
        body: { preferred }
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to update message preference', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error updating message preference:', error);
      throw error;
    }
  },

  calculateCredits: async ({ conversationId, collectionId, userId, files, formattedMessage, signal } = {}) => {
    try {
      const data = await fetchWithAuth('/json/conversations/calculate-credits', {
        method: 'POST',
        signal,
        body: {
          conversationId: conversationId,
          collectionId: collectionId,
          userId: userId,
          files: files,
          ...formattedMessage,
        }
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to calculate credits', 400, data);
      }

      return {
        cost: data.cost,
        userCredits: data.userCredits
      };
    } catch (error) {
      console.error('Error calculating credits:', error);
      throw error;
    }
  },

  searchChats: async (query, page = 1) => {
    const data = await fetchWithAuth(`/json/conversations/search?q=${encodeURIComponent(query)}&page=${page}`);
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to search conversations', 400, data);
    }

    return {
      chats: data.conversations,
      // Plus win-back: churned-Plus users get `isPlusLocked: true`
      // placeholder rows interleaved with normal matches. Surface the
      // lock payload so the sidebar can render them as opaque rows
      // without revealing titles. See backend/utils/plus_lock.js.
      plusLock: data.plusLock || null,
      pagination: {
        current: data.pagination.current || data.pagination.page || 1,
        pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.perPage),
        total: data.pagination.total,
        perPage: data.pagination.perPage,
        hasMore: data.pagination.hasMore
      }
    };
  },

  searchMessages: async (query, { page = 1, conversationIds = null } = {}) => {
    let url = `/json/conversations/search/messages?q=${encodeURIComponent(query)}&page=${page}`;
    if (Array.isArray(conversationIds) && conversationIds.length > 0) {
      url += `&conversationIds=${conversationIds.join(',')}`;
    }
    const data = await fetchWithAuth(url);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to search messages', 400, data);
    }

    return {
      results: data.results,
      mode: data.mode,
      pagination: {
        current: data.pagination.current || data.pagination.page || 1,
        pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.perPage),
        total: data.pagination.total,
        perPage: data.pagination.perPage,
        hasMore: data.pagination.hasMore,
      },
    };
  },

  importConversations: async (source, conversations, { shouldContinue } = {}) => {
    const IMPORT_TIMEOUT_MS = 300000;
    const MAX_ATTEMPTS = 5;
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const mergeImportResults = (results) =>
      results.reduce(
        (acc, result) => ({
          success: acc.success && result.success !== false,
          stopped: acc.stopped || result.stopped,
          imported: acc.imported + (result.imported || 0),
          skipped: acc.skipped + (result.skipped || 0),
          errors: [...acc.errors, ...(result.errors || [])],
          conversations: [...acc.conversations, ...(result.conversations || [])],
        }),
        {
          success: true,
          imported: 0,
          skipped: 0,
          errors: [],
          conversations: [],
        }
      );

    const normalizeImportError = (err) => {
      if (err instanceof ApiError) {
        return err;
      }

      const isAbort = err?.name === 'AbortError';
      if (isAbort) {
        return new ApiError(
          'Import timed out. Your connection may be slow — try again in a moment.',
          0
        );
      }

      return new ApiError(
        /failed to fetch/i.test(err?.message || '')
          ? 'Connection lost during import. Check your network and try again.'
          : err?.message || 'Failed to connect to server during import.',
        0
      );
    };

    const shouldSplitImportBatch = (err, conversationBatch) => {
      if (!Array.isArray(conversationBatch) || conversationBatch.length <= 1) {
        return false;
      }

      if (!(err instanceof ApiError)) {
        return false;
      }

      if (err.status === 401 || err.status === 429) {
        return false;
      }

      return (
        err.status === 0 ||
        err.status === 408 ||
        err.status === 413 ||
        err.status >= 500
      );
    };

    const postOnce = async (conversationBatch) => {
      const checkImportActive = () => {
        if (shouldContinue && !shouldContinue()) {
          throw new ApiError('Import stopped before uploading. Review your selection to continue.', 403);
        }
      };
      checkImportActive();
      const authStore = getAuthStore().getState();
      let token;
      try {
        token = await authStore.getValidToken?.() || getAuthStore().getState().token;
      } catch {
        throw new ApiError('Your session expired. Sign in again and retry the import.', 401);
      }
      // Token refresh can span a sign-out or account switch. Recheck before every
      // request, including recursive splits and retries of local history uploads.
      checkImportActive();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), IMPORT_TIMEOUT_MS);
      try {
        const response = await fetch(`${API_HOST}/json/conversations/import`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Platform': Platform.OS,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ source, conversations: conversationBatch }),
          signal: controller.signal,
        });
        let data;
        try {
          data = await response.json();
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          const messages = {
            401: 'Your session expired. Sign in again and retry the import.',
            403: 'The server blocked this import (HTTP 403). Contact support if this continues.',
            413: 'This upload exceeds the server upload limit (HTTP 413). Try a smaller conversation file.',
            429: 'Too many imports. Wait before trying again.',
            502: 'The import server is unreachable (HTTP 502). Please try again.',
            503: 'The import server is temporarily unavailable (HTTP 503). Please try again.',
            504: 'The import timed out at the gateway (HTTP 504). Retry the import; duplicates will be skipped.',
          };
          throw new ApiError(messages[response.status] ||
            `The import server returned an unreadable response (HTTP ${response.status}). Please retry or contact support.`, response.status);
        }

        if (data?.error || data?.success === false) {
          throw new ApiError(data.message || 'Failed to import conversations', response.status, data);
        }
        if (!response.ok) {
          throw new ApiError(
            data?.message || `Import failed (${response.status})`,
            response.status,
            data
          );
        }

        if (!data || !Number.isInteger(data.imported) || data.imported < 0 ||
            !Number.isInteger(data.skipped) || data.skipped < 0 || !Array.isArray(data.errors)) {
          throw new ApiError(`The import server returned an unexpected result (HTTP ${response.status}). Please retry or contact support.`, response.status);
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    };

    async function splitBatchAndImport(conversationBatch) {
      const midpoint = Math.floor(conversationBatch.length / 2);
      const leftBatch = conversationBatch.slice(0, midpoint);
      const rightBatch = conversationBatch.slice(midpoint);

      const results = [];
      for (const batch of [leftBatch, rightBatch]) {
        try {
          const result = await postWithRetries(batch);
          results.push(result);
          if (result.stopped) break;
        } catch (error) {
          const stopped = error.status === 401 || error.status === 403 || error.status === 429;
          results.push({ success: false, stopped, errors: batch.map(conv => ({
            title: conv.title, error: error.message,
          })) });
          if (stopped) break;
        }
      }
      return mergeImportResults(results);
    }

    async function postWithRetries(conversationBatch) {
      let lastError;

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          return await postOnce(conversationBatch);
        } catch (err) {
          lastError = err;
          if (err instanceof ApiError && err.status === 401) {
            throw err;
          }
          if (err instanceof ApiError && err.status === 429) {
            throw err;
          }

          const isAbort = err?.name === 'AbortError';
          const isNetwork =
            !(err instanceof ApiError) &&
            (err?.name === 'TypeError' ||
              /network|failed to fetch/i.test(err?.message || ''));

          if (isAbort && attempt < MAX_ATTEMPTS - 1) {
            await sleep(4000 + attempt * 2000);
            continue;
          }
          if (isNetwork && attempt < MAX_ATTEMPTS - 1) {
            await sleep(1800 * Math.pow(2, attempt));
            continue;
          }

          break;
        }
      }

      const normalizedError = normalizeImportError(lastError);
      if (shouldSplitImportBatch(normalizedError, conversationBatch)) {
        return splitBatchAndImport(conversationBatch);
      }

      throw normalizedError;
    }

    return postWithRetries(conversations);
  },
};

export const inferencesApi = {
  pollJob: async (inferenceId, jobId) => {
    try {
      const data = await fetchWithAuth(`/json/inferences/${inferenceId}/jobs/${jobId}/poll`);

      if (data.error || data.success === false) {
        throw new ApiError(data.message || 'Failed to poll inference job', 400, data);
      }

      const payload = data?.data || {};

      return {
        job: payload.job || data.job || payload || data,
        conversation: payload.conversation || null,
      };
    } catch (error) {
      console.error('Error polling inference job:', error);
      throw error;
    }
  }
};
